<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import assignmentPoliciesAPI from 'dashboard/api/assignmentPolicies';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SettingsSection from '../../../../../components/SettingsSection.vue';

export default {
  components: {
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isEnterprise } = useConfig();
    const { uiSettings, updateUISettings } = useUISettings();

    return { v$: useVuelidate(), isEnterprise, uiSettings, updateUISettings };
  },
  data() {
    return {
      selectedAgents: [],
      selectedTeamIds: [],
      teamMemberAgentIds: [],
      isAgentListUpdating: false,
      enableAutoAssignment: false,
      maxAssignmentLimit: null,
      isHydratingTeamFilter: false,
      assignmentPolicy: null,
      availableAssignmentPolicies: [],
      selectedAssignmentPolicy: null,
      isLoadingAssignmentPolicy: false,
      isLinkingAssignmentPolicy: false,
      isDeletingAssignmentPolicy: false,
      showDeletePolicyConfirmation: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      teamList: 'teams/getTeams',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      capacityPolicies: 'agentCapacityPolicies/getAgentCapacityPolicies',
      capacityPoliciesUIFlags: 'agentCapacityPolicies/getUIFlags',
    }),
    hasAssignmentV2() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.ASSIGNMENT_V2
      );
    },
    hasAdvancedAssignment() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.ADVANCED_ASSIGNMENT
      );
    },
    assignmentOrderLabel() {
      if (!this.assignmentPolicy) return '';

      const priority = this.assignmentPolicy.conversation_priority;
      if (priority === 'earliest_created') {
        return this.$t('INBOX_MGMT.ASSIGNMENT.PRIORITY.EARLIEST_CREATED');
      }
      if (priority === 'longest_waiting') {
        return this.$t('INBOX_MGMT.ASSIGNMENT.PRIORITY.LONGEST_WAITING');
      }
      return priority;
    },
    capacityPoliciesForInbox() {
      return (this.capacityPolicies || [])
        .flatMap(policy => {
          const limit = policy.inboxCapacityLimits?.find(
            item => item.inboxId === this.inbox.id
          );
          return limit ? [{ policy, limit }] : [];
        })
        .sort((left, right) => left.limit.id - right.limit.id);
    },
    isLoadingCapacityPolicies() {
      return this.capacityPoliciesUIFlags?.isFetching;
    },
    defaultCapacityPolicyId() {
      return this.capacityPoliciesForInbox[0]?.policy.id;
    },
    selectedTeams: {
      get() {
        return this.teamList.filter(team =>
          this.selectedTeamIds.includes(team.id)
        );
      },
      set(teams) {
        this.selectedTeamIds = teams.map(team => team.id);
      },
    },
    filteredAgentList() {
      if (!this.selectedTeamIds.length) {
        return this.agentList;
      }

      const filteredAgents = this.agentList.filter(agent =>
        this.teamMemberAgentIds.includes(agent.id)
      );

      const selectedAgentsOutsideFilter = this.selectedAgents.filter(
        selectedAgent =>
          !filteredAgents.some(
            filteredAgent => filteredAgent.id === selectedAgent.id
          )
      );

      return [...selectedAgentsOutsideFilter, ...filteredAgents];
    },
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return this.$t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    selectedTeamIds: {
      async handler() {
        if (this.isHydratingTeamFilter) {
          return;
        }
        this.saveTeamFilterSettings();
        await this.fetchTeamMembersForSelectedTeams();
      },
      deep: true,
    },
  },
  async mounted() {
    this.$store.dispatch('agents/get');
    await this.$store.dispatch('teams/get');
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.enableAutoAssignment = this.inbox.enable_auto_assignment;
      this.maxAssignmentLimit =
        this.inbox?.auto_assignment_config?.max_assignment_limit || null;
      this.initializeTeamFilterFromSettings();
      this.fetchAttachedAgents();
      if (this.hasAssignmentV2) {
        this.fetchAssignmentPolicy();
        if (this.hasAdvancedAssignment) {
          this.fetchAvailableAssignmentPolicies();
          this.fetchCapacityPolicies();
        }
      } else {
        this.assignmentPolicy = null;
        this.availableAssignmentPolicies = [];
        this.selectedAssignmentPolicy = null;
      }
    },
    initializeTeamFilterFromSettings() {
      const storedTeamFilter =
        this.uiSettings?.inbox_collaborators_team_filter?.[this.inbox.id] || [];
      this.isHydratingTeamFilter = true;
      this.selectedTeamIds = storedTeamFilter;
      this.fetchTeamMembersForSelectedTeams().finally(() => {
        this.isHydratingTeamFilter = false;
      });
    },
    saveTeamFilterSettings() {
      const currentTeamFilters =
        this.uiSettings?.inbox_collaborators_team_filter || {};
      this.updateUISettings({
        inbox_collaborators_team_filter: {
          ...currentTeamFilters,
          [this.inbox.id]: this.selectedTeamIds,
        },
      });
    },
    async fetchTeamMembersForSelectedTeams() {
      if (!this.selectedTeamIds.length) {
        this.teamMemberAgentIds = [];
        return;
      }

      await Promise.allSettled(
        this.selectedTeamIds.map(teamId =>
          this.$store.dispatch('teamMembers/get', {
            teamId,
          })
        )
      );

      const uniqueAgentIds = new Set();
      this.selectedTeamIds.forEach(teamId => {
        const teamMembers =
          this.$store.getters['teamMembers/getTeamMembers'](teamId);
        teamMembers.forEach(member => uniqueAgentIds.add(member.id));
      });
      this.teamMemberAgentIds = [...uniqueAgentIds];
    },
    async fetchAttachedAgents() {
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.selectedAgents = inboxMembers;
      } catch (error) {
        //  Handle error
      }
    },
    async fetchAssignmentPolicy() {
      if (!this.inbox.id) return;

      this.isLoadingAssignmentPolicy = true;
      try {
        const response = await assignmentPoliciesAPI.getInboxPolicy(
          this.inbox.id
        );
        this.assignmentPolicy = response.data;
      } catch (error) {
        this.assignmentPolicy = null;
      } finally {
        this.isLoadingAssignmentPolicy = false;
      }
    },
    async fetchCapacityPolicies() {
      try {
        await this.$store.dispatch('agentCapacityPolicies/get');
      } catch (error) {
        // The empty state communicates that capacity information is unavailable.
      }
    },
    async fetchAvailableAssignmentPolicies() {
      try {
        const response = await assignmentPoliciesAPI.get();
        this.availableAssignmentPolicies = response.data;
      } catch (error) {
        this.availableAssignmentPolicies = [];
      }
    },
    async linkAssignmentPolicy(policy) {
      if (!policy?.id) return;

      this.isLinkingAssignmentPolicy = true;
      try {
        await assignmentPoliciesAPI.setInboxPolicy(this.inbox.id, policy.id);
        this.assignmentPolicy = policy;
        this.selectedAssignmentPolicy = null;
        useAlert(this.$t('INBOX_MGMT.ASSIGNMENT.LINK_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ASSIGNMENT.LINK_ERROR'));
      } finally {
        this.isLinkingAssignmentPolicy = false;
      }
    },
    navigateToCreateAssignmentPolicy() {
      this.$router.push({
        name: 'agent_assignment_policy_create',
        params: { accountId: this.accountId },
        query: { inboxId: this.inbox.id },
      });
    },
    navigateToEditAssignmentPolicy() {
      if (!this.assignmentPolicy?.id) return;

      this.$router.push({
        name: 'agent_assignment_policy_edit',
        params: {
          accountId: this.accountId,
          id: this.assignmentPolicy.id,
        },
      });
    },
    navigateToAssignmentPolicies() {
      this.$router.push({
        name: 'agent_assignment_policy_index',
        params: { accountId: this.accountId },
      });
    },
    confirmDeleteAssignmentPolicy() {
      this.showDeletePolicyConfirmation = true;
    },
    closeDeletePolicyConfirmation() {
      this.showDeletePolicyConfirmation = false;
    },
    navigateToCapacityPolicy(policyId) {
      if (!policyId) return;

      this.$router.push({
        name: 'agent_capacity_policy_edit',
        params: {
          accountId: this.accountId,
          id: policyId,
        },
      });
    },
    async deleteAssignmentPolicy() {
      if (this.isDeletingAssignmentPolicy) return;

      this.isDeletingAssignmentPolicy = true;
      try {
        await assignmentPoliciesAPI.removeInboxPolicy(this.inbox.id);
        this.assignmentPolicy = null;
        this.showDeletePolicyConfirmation = false;
        useAlert(this.$t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_ERROR'));
      } finally {
        this.isDeletingAssignmentPolicy = false;
      }
    },
    handleEnableAutoAssignment() {
      this.updateInbox();
    },
    async updateAgents() {
      const agentList = this.selectedAgents.map(el => el.id);
      this.isAgentListUpdating = true;
      try {
        await this.$store.dispatch('inboxMembers/create', {
          inboxId: this.inbox.id,
          agentList,
        });
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isAgentListUpdating = false;
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          enable_auto_assignment: this.enableAutoAssignment,
          auto_assignment_config: {
            max_assignment_limit: this.maxAssignmentLimit,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    maxAssignmentLimit: {
      minValue: minValue(1),
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
    >
      <multiselect
        v-model="selectedTeams"
        :options="teamList"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        :placeholder="$t('FORMS.MULTISELECT.SELECT')"
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
      />

      <multiselect
        v-model="selectedAgents"
        :options="filteredAgentList"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        placeholder="Pick some"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        @select="v$.selectedAgents.$touch"
      />

      <woot-submit-button
        :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :loading="isAgentListUpdating"
        @click="updateAgents"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT_SUB_TEXT')"
    >
      <template v-if="hasAssignmentV2">
        <div v-if="isLoadingAssignmentPolicy" class="assignment-policy-card">
          <span class="spinner" />
        </div>

        <div v-else-if="assignmentPolicy" class="assignment-policy-card">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h4
                class="mb-1 text-base font-medium text-slate-900 dark:text-slate-100"
              >
                {{ assignmentPolicy.name }}
              </h4>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('INBOX_MGMT.ASSIGNMENT.POLICY_LABEL') }}
              </p>
            </div>
            <woot-button
              variant="clear"
              color-scheme="alert"
              icon="delete"
              @click="confirmDeleteAssignmentPolicy"
            />
          </div>

          <ul class="assignment-rules">
            <li>{{ assignmentOrderLabel }}</li>
            <li>{{ $t('INBOX_MGMT.ASSIGNMENT.METHOD.BALANCED') }}</li>
          </ul>

          <woot-button
            variant="clear"
            icon="arrow-right"
            @click="navigateToEditAssignmentPolicy"
          >
            {{ $t('INBOX_MGMT.ASSIGNMENT.CUSTOMIZE_POLICY') }}
          </woot-button>
        </div>

        <div v-else class="assignment-policy-card">
          <h4
            class="mb-1 text-base font-medium text-slate-900 dark:text-slate-100"
          >
            {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULES_TITLE') }}
          </h4>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULES_DESCRIPTION') }}
          </p>

          <ul class="assignment-rules">
            <li>{{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_1') }}</li>
            <li>{{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_2') }}</li>
          </ul>

          <template v-if="hasAdvancedAssignment">
            <multiselect
              v-model="selectedAssignmentPolicy"
              :options="availableAssignmentPolicies"
              track-by="id"
              label="name"
              :loading="isLinkingAssignmentPolicy"
              :disabled="isLinkingAssignmentPolicy"
              :placeholder="$t('INBOX_MGMT.ASSIGNMENT.LINK_EXISTING_POLICY')"
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              @select="linkAssignmentPolicy"
            />

            <div class="flex flex-wrap gap-2 pt-4">
              <woot-button
                variant="smooth"
                icon="add"
                @click="navigateToCreateAssignmentPolicy"
              >
                {{ $t('INBOX_MGMT.ASSIGNMENT.CREATE_NEW_POLICY') }}
              </woot-button>
              <woot-button
                variant="clear"
                @click="navigateToAssignmentPolicies"
              >
                {{ $t('INBOX_MGMT.ASSIGNMENT.VIEW_ALL_POLICIES') }}
              </woot-button>
            </div>
          </template>

          <p v-else class="pt-4 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ASSIGNMENT.UPGRADE_PROMPT') }}
          </p>
        </div>
      </template>
      <div
        v-if="hasAssignmentV2 && hasAdvancedAssignment"
        class="assignment-policy-card"
      >
        <h4
          class="mb-1 text-base font-medium text-slate-900 dark:text-slate-100"
        >
          {{ $t('INBOX_MGMT.ASSIGNMENT.CAPACITY.TITLE') }}
        </h4>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ASSIGNMENT.CAPACITY.DESCRIPTION') }}
        </p>

        <div
          v-if="isLoadingCapacityPolicies"
          class="flex items-center justify-center py-4"
        >
          <span class="spinner" />
        </div>
        <p
          v-else-if="!capacityPoliciesForInbox.length"
          class="pt-3 text-sm text-slate-600 dark:text-slate-400"
        >
          {{ $t('INBOX_MGMT.ASSIGNMENT.CAPACITY.NO_POLICY') }}
        </p>
        <div v-else class="capacity-policy-list">
          <button
            v-for="entry in capacityPoliciesForInbox"
            :key="entry.limit.id"
            type="button"
            class="capacity-policy-row"
            :title="$t('INBOX_MGMT.ASSIGNMENT.CAPACITY.EDIT')"
            @click="navigateToCapacityPolicy(entry.policy.id)"
          >
            <span class="flex min-w-0 flex-col text-left">
              <span
                class="truncate font-medium text-slate-900 dark:text-slate-100"
              >
                {{ entry.policy.name }}
              </span>
              <span class="text-sm text-slate-600 dark:text-slate-400">
                {{
                  $t('INBOX_MGMT.ASSIGNMENT.CAPACITY.LIMIT', {
                    count: entry.limit.conversationLimit,
                  })
                }}
              </span>
            </span>
            <span class="capacity-policy-badge">
              {{
                $t(
                  entry.policy.id === defaultCapacityPolicyId
                    ? 'INBOX_MGMT.ASSIGNMENT.CAPACITY.DEFAULT'
                    : 'INBOX_MGMT.ASSIGNMENT.CAPACITY.OVERRIDE'
                )
              }}
            </span>
          </button>
        </div>
      </div>
      <div v-if="!hasAssignmentV2" class="max-assignment-container">
        <woot-input
          v-model="maxAssignmentLimit"
          type="number"
          :class="{ error: v$.maxAssignmentLimit.$error }"
          :error="maxAssignmentLimitErrors"
          :label="$t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')"
          @blur="v$.maxAssignmentLimit.$touch"
        />

        <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT') }}
        </p>

        <woot-submit-button
          :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :disabled="v$.maxAssignmentLimit.$invalid"
          @click="updateInbox"
        />
      </div>

      <label class="w-3/4 pt-4 settings-item">
        <div class="flex items-center gap-2">
          <input
            id="enableAutoAssignment"
            v-model="enableAutoAssignment"
            type="checkbox"
            @change="handleEnableAutoAssignment"
          />
          <label for="enableAutoAssignment">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') }}
        </p>
      </label>
    </SettingsSection>
    <woot-modal
      :show="showDeletePolicyConfirmation"
      :on-close="closeDeletePolicyConfirmation"
    >
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_TITLE')"
      />
      <div class="px-8 pb-8">
        <p class="mb-6 text-sm text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_MESSAGE') }}
        </p>
        <div class="flex justify-end gap-2">
          <woot-button variant="clear" @click="closeDeletePolicyConfirmation">
            {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.CANCEL') }}
          </woot-button>
          <woot-button
            color-scheme="alert"
            :is-loading="isDeletingAssignmentPolicy"
            @click="deleteAssignmentPolicy"
          >
            {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.CONFIRM_DELETE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';
@import 'dashboard/assets/scss/mixins';

.max-assignment-container {
  padding-top: var(--space-slab);
  padding-bottom: var(--space-slab);
}

.assignment-policy-card {
  margin-top: 0;
  padding: var(--space-normal);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  background: var(--white);

  .dark & {
    border-color: var(--s-700);
    background: var(--s-800);
  }
}

.assignment-policy-card + .assignment-policy-card {
  margin-top: var(--space-normal);
}

.capacity-policy-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-normal);
}

.capacity-policy-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-normal);
  width: 100%;
  padding: var(--space-small) var(--space-normal);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  background: transparent;

  &:hover {
    background: var(--s-25);
  }

  .dark & {
    border-color: var(--s-700);

    &:hover {
      background: var(--s-700);
    }
  }
}

.capacity-policy-badge {
  flex-shrink: 0;
  padding: var(--space-micro) var(--space-small);
  border-radius: var(--border-radius-pill);
  color: var(--s-700);
  background: var(--s-100);
  font-size: var(--font-size-mini);

  .dark & {
    color: var(--s-200);
    background: var(--s-700);
  }
}

.assignment-rules {
  margin: var(--space-normal) 0;
  padding-left: var(--space-normal);
  list-style: disc;
  color: var(--s-600);

  .dark & {
    color: var(--s-300);
  }
}
</style>
